import axios from "axios";
import { create } from "zustand";
import api from "../service/api"; // API servisinizin içe aktarıldığını varsayar
import { ListQuery, QueryParams } from "../types"; // ListQuery, QueryParams'ın types dosyanızda olduğunu varsayar

// --- TİP TANIMLARI ---

// Gelen verinin mutlaka bir ID içermesi gerekir (Güncelleme ve Silme için)
interface WithId { id: number | string }

// API'den dönen liste yapısı
export interface ListApiResult<T> {
  items: T[];
  total: number;
}

// Hata detay nesnesi (Tsoa/Validator'dan gelen)
interface ApiErrorDetail {
  message: string;
  value?: any;
}

// Store'da saklanacak hata yapısı
interface StoreError {
  message: string;
  details?: Record<string, ApiErrorDetail> | null;
}

// Ana Generic Store arayüzü
export interface GenericStore<T> {
  data: T[];
  total: number;
  loading: boolean;
  error: StoreError | null; // <-- Detaylı hata tipi
  queryParams: QueryParams;

  // Metotlar artık API yanıtını Promise olarak döndürüyor
  fetchData: () => Promise<T[]>;
  fetchDataDynamic: (params?: QueryParams) => Promise<ListApiResult<T>>;
  setQueryParams: (params: QueryParams) => void;

  createItem: (item: Partial<T>) => Promise<T>;
  updateItem: (id: number | string, item: Partial<T>) => Promise<T>;
  deleteItem: (id: number | string) => Promise<any>;
}

// --- ZUSTAND STORE TANIMI ---

/**
 * Belirtilen bir endpoint için CRUD ve Listeleme (DynamicQuery) işlemlerini yöneten
 * genel bir Zustand store'u oluşturur.
 * @param endpoint - API endpoint adresi (örn: 'stock')
 */
export const createApiStore = <T extends WithId>(endpoint: string) =>
  create<GenericStore<T>>((set, get) => {
    const savedParams = localStorage.getItem(`${endpoint}_query`);
    const initialParams: QueryParams = savedParams
      ? JSON.parse(savedParams)
      : { pageIndex: 1, pageSize: 10, sort: [], filter: undefined };

    let activeAbortController: AbortController | null = null;
    let latestRequestId = 0;

    // 💡 Hata Yönetimi Yardımcı Fonksiyonu
    const handleError = (err: any, defaultMessage: string) => {
      const errorMessage = err.response?.data?.message || err.message || defaultMessage;
      const errorDetails = err.response?.data?.details; // Tsoa/Validator detayları

      const errorPayload: StoreError = {
        message: errorMessage,
        details: errorDetails || null,
      };

      set({ error: errorPayload, loading: false });
      throw err; // Hatanın yakalanması için tekrar fırlat
    };

    return {
      data: [],
      total: 0,
      loading: false,
      error: null,
      queryParams: initialParams,

      setQueryParams: (params) => {
        set({ queryParams: params });
        localStorage.setItem(`${endpoint}_query`, JSON.stringify(params));
      },

      // --- DYNAMIC FETCH ---
      fetchDataDynamic: async (params: any) => {
        // Önceki bekleyen isteği iptal et ve yeni istek sayacı oluştur
        if (activeAbortController) {
          activeAbortController.abort();
        }
        const currentController = new AbortController();
        activeAbortController = currentController;
        const currentRequestId = ++latestRequestId;

        if (params && !params.silent) {
          set({ queryParams: params });
          localStorage.setItem(`${endpoint}_query`, JSON.stringify(params));
        } else if (params && params.silent) {
          // We might not want to save silent params as global queryParams, or maybe we do,
          // but definitely we can use it.
          const { silent, ...restParams } = params;
          if (Object.keys(restParams).length > 0) {
            set({ queryParams: { ...get().queryParams, ...restParams } });
          }
        }
        const finalParams = get().queryParams;

        if (!(params && params.silent)) {
          set({ loading: true, error: null });
        } else {
          set({ error: null });
        }

        // Tsoa Controller'ın beklediği URL Query parametreleri
        const urlParams = {
          pageIndex: finalParams.pageIndex || 1,
          pageSize: finalParams.pageSize || 10,
          showDeleted: false,
        };

        // Tsoa Controller'ın beklediği Body (ListQuery)
        const requestBody: ListQuery = {
          sort: finalParams.sort || [],
          filter: finalParams.filter
            ? { logic: "and", filters: [finalParams.filter] }
            : { logic: "and", filters: [] },
        };

        try {
          const res = await api.post<ListApiResult<T> | T[]>(`${endpoint}/DynamicQuery`, requestBody, {
            params: urlParams,
            headers: { "x-silent": "true" },
            signal: currentController.signal,
          });

          // İstek tamamlandığında, araya daha yeni bir istek girmişse veya iptal edilmişse state'i güncelleme (Race condition önleyici)
          if (currentRequestId !== latestRequestId || currentController.signal.aborted) {
            return { items: [], total: 0 };
          }

          const response = res.data;

          let responseData: T[] = Array.isArray(response) ? response : (response.items || []);
          const originalLength = responseData.length;

          // Soft-delete olan kayıtları frontend tarafında gizle
          responseData = responseData.filter((item: any) => !item.deletedAt);

          let responseTotal: number = Array.isArray(response) ? response.length : (response.total || 0);

          // Eğer sunucudan total geldiyse ve biz clientta filtreleme yaptıysak
          if (!Array.isArray(response) && originalLength > responseData.length) {
            responseTotal -= (originalLength - responseData.length);
          }

          set({
            data: responseData,
            total: responseTotal,
            loading: false,
            queryParams: finalParams,
          });
          localStorage.setItem(`${endpoint}_query`, JSON.stringify(finalParams));
          return { items: responseData, total: responseTotal }; // API yanıtını döndür
        } catch (err: any) {
          // İstek iptal edildiyse veya eski bir istekse sessizce yut
          if (axios.isCancel(err) || err?.name === "CanceledError" || currentRequestId !== latestRequestId || currentController.signal.aborted) {
            return { items: [], total: 0 };
          }
          handleError(err, "Veri çekilirken bilinmeyen bir hata oluştu");
          return { items: [], total: 0 }; // Typescript için dönüş
        }
      },

      // --- SIMPLE FETCH ---
      fetchData: async (): Promise<T[]> => {
        set({ loading: true, error: null });
        try {
          const res = await api.get<T[]>(`${endpoint}/all`, {
            headers: { "x-silent": "true" }
          });
          const data = res.data.filter((item: any) => !item.deletedAt);
          set({ data, loading: false });
          return data;
        } catch (err: any) {
          handleError(err, "Veri çekilirken hata oluştu");
          return [];
        }
      },

      // --- CREATE ---
      createItem: async (item: Partial<T>): Promise<T> => {
        try {
          set({ loading: true, error: null });
          const res = await api.post<T>(endpoint, item);
          console.log("Oluşturulan Kayıt:", res.data); // Oluşturulan kaydı logla
          await get().fetchDataDynamic();
          set({ loading: false });
          return res.data; // Başarılı dönüş
        } catch (err: any) {
          handleError(err, "Kayıt oluşturulurken hata oluştu");
          console.error("Oluşturma Hatası Detayı:", err.response?.data); // Hata detaylarını logla
          throw err; // 💡 Hata burada açıkça fırlatılmalı
        }
      },

      // --- UPDATE ---
      updateItem: async (id: number | string, item: Partial<T>): Promise<T> => {
        try {
          set({ loading: true, error: null });
          const res = await api.put<T>(`${endpoint}/${id}`, item);

          await get().fetchDataDynamic();
          set({ loading: false });
          return res.data; // Başarılı dönüş
        } catch (err: any) {
          handleError(err, "Kayıt güncellenirken hata oluştu");
          throw err; // 💡 Hata burada açıkça fırlatılmalı
        }
      },

      // --- DELETE ---
      deleteItem: async (id: number | string): Promise<any> => {
        try {
          set({ loading: true, error: null });
          const res = await api.delete(`${endpoint}/${id}`);

          await get().fetchDataDynamic();
          set({ loading: false });
          return res.data; // Başarılı dönüş
        } catch (err: any) {
          handleError(err, "Kayıt silinirken hata oluştu");
          throw err; // 💡 Hata burada açıkça fırlatılmalı
        }
      },
    };
  });